import { Plus, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { VeeraThreadList } from "@/components/chat/VeeraThreadList";
import { CoachLensStatusBadge } from "@/components/chat/CoachLensStatusBadge";
import type { CoachLensStatus } from "@/lib/behavior/coachLensStatus";

type ThreadSummary = {
  id: string;
  title: string;
  preview: string;
  updatedAt: string;
  tagLabel?: string;
};

interface CoachContextSidebarProps {
  activeWorkType: "global" | "leader" | "coach";
  activeThreadId: string | null;
  onThreadSelect: (threadId: string) => void;
  onNewThread: () => void;
  /** Called when the user selects a different work type via the segmented control. */
  onWorkTypeSelect?: (workType: "global" | "leader" | "coach") => void;
  newThreadLabel: string;
  threads: ThreadSummary[];
  /** If provided, renders a CoachLensStatusBadge (navigates to /coach/identity on click). */
  coachLensStatus?: CoachLensStatus;
  disabled?: boolean;
  className?: string;
}

const WORK_TYPE_OPTIONS = [
  { value: "coach" as const, label: "My Coaching" },
  { value: "leader" as const, label: "Leaders" },
  { value: "global" as const, label: "Portfolio" },
] as const;

const WORKSPACE_LABELS: Record<CoachContextSidebarProps["activeWorkType"], string> = {
  global: "Portfolio",
  leader: "Leaders",
  coach: "My Coaching",
};

export function CoachContextSidebar({
  activeWorkType,
  activeThreadId,
  onThreadSelect,
  onNewThread,
  onWorkTypeSelect,
  newThreadLabel,
  threads,
  coachLensStatus,
  disabled = false,
  className,
}: CoachContextSidebarProps) {
  const navigate = useNavigate();

  return (
    <aside className={cn("flex h-full w-[17.5rem] shrink-0 flex-col border-r border-border bg-card", className)}>
      <div className="border-b border-border px-4 py-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <p className="text-sm font-semibold">Coach Veera</p>
        </div>
      </div>

      {onWorkTypeSelect ? (
        <div className="border-b border-border px-3 py-2">
          <div className="flex rounded-lg bg-muted/50 p-0.5">
            {WORK_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onWorkTypeSelect(option.value)}
                disabled={disabled}
                className={cn(
                  "flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                  activeWorkType === option.value
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="px-4 py-4">
        <button
          type="button"
          onClick={onNewThread}
          disabled={disabled}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 px-4 py-3 text-sm font-medium text-white transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          {newThreadLabel}
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-4 pb-3">
        <p className="pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {WORKSPACE_LABELS[activeWorkType]}
        </p>
        <ScrollArea className="min-h-0 flex-1 pr-1">
          {threads.length > 0 ? (
            <VeeraThreadList
              variant="detailed"
              threads={threads}
              activeThreadId={activeThreadId}
              onThreadSelect={onThreadSelect}
              disabled={disabled}
            />
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-muted/20 px-3 py-4 text-sm text-muted-foreground">
              Start a new thread for this workspace.
            </div>
          )}
        </ScrollArea>
      </div>

      {coachLensStatus != null ? (
        <div className="border-t border-border px-4 py-3">
          <CoachLensStatusBadge
            status={coachLensStatus}
            onClick={() => navigate("/coach/identity")}
            disabled={disabled}
          />
        </div>
      ) : null}
    </aside>
  );
}
